import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { CloseIcon } from '@/components/icons'

/**
 * 标签与徽章。
 *
 * 区分三种形态，因为交互语义不同，不能用一个组件糊过去：
 *   - `Badge`      纯展示（详情页顶部的「整合包」「v2.4.1」）；
 *   - `ChipButton` 可切换的筛选项（搜索页左栏的「科技」「4K」）；
 *   - `RemovableChip` 已生效的筛选条件，带删除按钮（搜索页顶部的「材质 › 4K ×」）。
 */

export type BadgeTone = 'primary' | 'neutral' | 'outline'

const BADGE_TONES: Record<BadgeTone, string> = {
  primary: 'bg-primary-soft text-primary',
  neutral: 'bg-surface-muted text-muted-foreground',
  outline: 'border border-border text-muted-foreground',
}

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: BadgeTone
  className?: string
  children: ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

export interface ChipButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean
}

/**
 * 可切换的筛选项。
 *
 * `aria-pressed` 而不是 `aria-selected`：它是一个独立的开关按钮，不属于
 * listbox/tab 结构。读屏器会念「已按下 / 未按下」，正是筛选项的语义。
 */
export function ChipButton({ active = false, className, children, ...props }: ChipButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        'inline-flex h-8 items-center rounded-lg border px-3 text-[0.8125rem] font-medium transition-colors duration-150',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-surface text-muted-foreground hover:border-border-strong hover:text-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

/** 已生效的筛选条件。删除按钮是独立的 `<button>`，避免整块可点导致误删。 */
export function RemovableChip({
  label,
  onRemove,
  className,
}: {
  label: string
  onRemove: () => void
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex h-10 w-full items-center justify-between gap-2 rounded-card border border-border bg-surface px-3 text-sm font-medium text-foreground',
        className,
      )}
    >
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`移除筛选条件 ${label}`}
        className="-mr-1 shrink-0 rounded p-1 text-subtle-foreground transition-colors hover:text-foreground"
      >
        <CloseIcon className="size-3.5" />
      </button>
    </span>
  )
}

/**
 * 详情页/卡片上的标签，点击跳到对应的检索页。
 *
 * 目标地址由调用方传入而不是在这里拼：五栏落到三种不同的查询参数
 * （`tag_id` / `author_name` / `update_year`），统一由 `api/taxonomy.ts` 的
 * `searchPathFor*` 生成，这里只负责长相。
 */
export function TagLink({
  to,
  className,
  children,
}: {
  to: string
  className?: string
  children: ReactNode
}) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex h-7 items-center rounded-lg border border-border bg-surface px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary',
        className,
      )}
    >
      {children}
    </Link>
  )
}
